import { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Progress, Space, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, ReloadOutlined, ScanOutlined } from '@ant-design/icons';
import type { DuplicatePhonesApplyResult, DuplicatePhonesContact, PhoneListItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import { EmptyState } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useApplyDuplicatePhones, useDuplicatePhonesResults, useScanDuplicatePhones } from '../../features/cleanup/api';
import { fmtDateTime, plural } from '../../utils/format';
import { EntityTag } from '../../components/EntityTag';

const removedCount = (c: DuplicatePhonesContact) => c.groups.reduce((n, g) => n + g.remove.length, 0);

function PhoneTag({ phone, removed }: { phone: PhoneListItem; removed?: boolean }) {
  const extra = [phone.label, phone.isPrimary ? 'Primary' : null].filter(Boolean).join(' · ');
  return (
    <Tag color={removed ? 'error' : 'success'} style={{ marginInlineEnd: 0 }}>
      <span style={removed ? { textDecoration: 'line-through' } : undefined}>{phone.number}</span>
      {extra && <span style={{ opacity: 0.7 }}> {extra}</span>}
    </Tag>
  );
}

export function DuplicatePhonesPage() {
  const { message, modal } = App.useApp();
  const stored = useDuplicatePhonesResults();
  const scan = useScanDuplicatePhones();
  const apply = useApplyDuplicatePhones();
  const [progress, setProgress] = useState<{ scanned: number; total: number; found: number } | null>(null);
  const [applied, setApplied] = useState<{ done: number; total: number } | null>(null);
  const [outcome, setOutcome] = useState<DuplicatePhonesApplyResult | null>(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const data = stored.data;
  const all = data?.results ?? [];
  const totalRemovable = useMemo(() => all.reduce((n, c) => n + removedCount(c), 0), [all]);

  const rows = useMemo(() => {
    const needle = q.toLocaleLowerCase();
    const digits = q.replace(/\D/g, '');
    return all.filter(
      (c) =>
        !needle ||
        c.name.toLocaleLowerCase().includes(needle) ||
        (c.email ?? '').toLowerCase().includes(needle) ||
        (digits.length >= 3 && c.phones.some((p) => p.number.replace(/\D/g, '').includes(digits))),
    );
  }, [all, q]);

  const runScan = async () => {
    setSelected([]);
    setOutcome(null);
    setProgress({ scanned: 0, total: 0, found: 0 });
    try {
      stored.set(await scan.mutateAsync({ onProgress: setProgress }));
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setProgress(null);
    }
  };

  const runRemove = (target: DuplicatePhonesContact[]) => {
    if (!target.length || !data) return;
    const n = target.reduce((s, c) => s + removedCount(c), 0);
    modal.confirm({
      title: `Remove ${plural(n, 'duplicate number')} from ${plural(target.length, 'contact')}?`,
      content: (
        <div>
          <p>Each contact keeps one entry per number: the one written in full (with the country code), taking over the label and Primary flag of the ones removed.</p>
          <p style={{ marginBottom: 0 }}>The change shows on each contact's timeline.</p>
        </div>
      ),
      okText: 'Remove duplicates',
      okButtonProps: { danger: true },
      onOk: async () => {
        const ids = target.map((c) => c.id);
        setApplied({ done: 0, total: ids.length });
        try {
          const result = await apply.mutateAsync({ contactIds: ids, onProgress: (done) => setApplied({ done, total: ids.length }) });
          setOutcome(result);
          message.success(`Removed ${plural(result.removed, 'duplicate number')} from ${plural(result.contacts, 'contact')}`);
          const done = new Set(ids.filter((id) => !result.skipped.some((s) => s.contactId === id)));
          stored.set({ ...data, results: data.results.filter((c) => !done.has(c.id)) });
          setSelected([]);
        } catch (e) {
          message.error(errorMessage(e));
        } finally {
          setApplied(null);
        }
      },
    });
  };

  const selectedRows = rows.filter((c) => selected.includes(c.id));
  const percent = progress?.total ? Math.min(99, Math.round((progress.scanned / progress.total) * 100)) : 0;

  return (
    <div className="page">
      <PageHeader
        title="Duplicate Phones"
        subtitle="Find contacts that have the same phone number twice, with and without the country code, and remove the extra copies."
        extra={
          <Button type="primary" icon={data ? <ReloadOutlined /> : <ScanOutlined />} onClick={runScan} loading={scan.isPending}>
            {data ? 'Scan again' : 'Scan contacts'}
          </Button>
        }
      />
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        {progress ? (
          <Card title={<CardTitle title="Scanning" subtitle={`${progress.scanned.toLocaleString()} of ${plural(progress.total, 'contact')} checked · ${plural(progress.found, 'contact')} with duplicates so far`} />}>
            <Progress percent={percent} status="active" />
          </Card>
        ) : !data ? (
          <Card title={<CardTitle title="How it works" subtitle="Nothing is changed by a scan." />}>
            <Typography.Paragraph>
              The scan compares the numbers in each contact's Phones list. Two numbers count as the same when one ends with the other and the difference is just a
              dialling prefix such as <b>+357</b>, <b>00357</b> or a leading <b>0</b>, e.g. <b>35799761457</b> and <b>99761457</b>. Spaces, dashes and brackets
              are ignored.
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              You review what was found, then remove the duplicates for all or some contacts. The number written in full (with the country code) is kept, and it
              takes over the label and Primary flag of the copies removed.
            </Typography.Paragraph>
          </Card>
        ) : (
          <>
            <Card
              title={
                <CardTitle
                  title={all.length ? `${plural(totalRemovable, 'duplicate number')} on ${plural(all.length, 'contact')}` : 'No duplicate phones'}
                  subtitle={`${plural(data.scanned, 'contact')} checked ${fmtDateTime(data.finishedAt)}.`}
                />
              }
            >
              {!all.length && <Typography.Text type="secondary">No contact has the same phone number twice.</Typography.Text>}
              {all.length > 0 && (
                <Typography.Text type="secondary">
                  <Tag color="success">Kept</Tag> stays on the contact, <Tag color="error">struck out</Tag> is removed.
                </Typography.Text>
              )}
            </Card>

            {outcome && outcome.skipped.length > 0 && (
              <Alert
                type="warning"
                showIcon
                closable
                onClose={() => setOutcome(null)}
                title={`${plural(outcome.skipped.length, 'contact')} skipped.`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {outcome.skipped.slice(0, 20).map((s) => (
                      <li key={s.contactId}>
                        <EntityTag kind="contact" id={s.contactId}>Contact</EntityTag>: {s.reason}
                      </li>
                    ))}
                    {outcome.skipped.length > 20 && <li>and {plural(outcome.skipped.length - 20, 'more')}</li>}
                  </ul>
                }
              />
            )}

            {all.length > 0 && (
              <Card>
                <div className="table-toolbar">
                  <div className="toolbar-group">
                    <TableSearch value={q} onSearch={setQ} placeholder="Search name, email or number" width={260} />
                  </div>
                  <div className="toolbar-group">
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!(selected.length ? selectedRows.length : rows.length)}
                      loading={apply.isPending}
                      onClick={() => runRemove(selected.length ? selectedRows : rows)}
                    >
                      {selected.length ? `Remove duplicates on ${plural(selectedRows.length, 'selected contact')}` : `Remove duplicates on all ${rows.length.toLocaleString()} shown`}
                    </Button>
                  </div>
                </div>
                {applied && <Progress percent={Math.round((applied.done / applied.total) * 100)} status="active" style={{ marginBottom: 16 }} />}
                <Table<DuplicatePhonesContact>
                  rowKey="id"
                  dataSource={rows}
                  rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), preserveSelectedRowKeys: true }}
                  pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (n) => plural(n, 'contact') }}
                  locale={{ emptyText: <EmptyState description="No contacts match this search." /> }}
                  columns={[
                    {
                      title: 'Contact',
                      key: 'contact',
                      width: 260,
                      render: (_, c) => <NameCell name={c.name || '(no name)'} sub={c.email ?? 'No email'} to={`/contacts/${c.id}`} />,
                    },
                    {
                      title: 'Duplicates',
                      key: 'groups',
                      render: (_, c) => (
                        <Space orientation="vertical" size={6}>
                          {c.groups.map((g) => (
                            <Space key={g.keep.number} size={6} wrap>
                              <PhoneTag phone={g.keep} />
                              {g.remove.map((p, i) => (
                                <PhoneTag key={`${p.number}${i}`} phone={p} removed />
                              ))}
                            </Space>
                          ))}
                        </Space>
                      ),
                    },
                    {
                      title: 'Phones after',
                      key: 'after',
                      width: 140,
                      render: (_, c) => `${c.phones.length} → ${c.phones.length - removedCount(c)}`,
                    },
                  ]}
                />
              </Card>
            )}
          </>
        )}
      </Space>
    </div>
  );
}
